<template>
<div class="promotion-container">
    <div class="promotion">
        <div class="wordmark">
            <NuxtImg src="/images/promotions/bigbangbundle/bigbanglogo.png" />
        </div>
        <div class="description">
            <div v-if="!promotionActivated" class="header">Make it a Bundle & Save:</div>
            <div v-else class="header">Bundled!</div>
            <div v-if="!promotionActivated" class="promotion-inactive">
                <div>Add {{ upsaleProduct?.title }} for a big markdown!</div>
            </div>
            <div v-else class="promotion-active">
                <div>Includes The Jackbox Party Pack 11 and The Jackbox Survey Scramble.</div>
            </div>
        </div>
        <div class="toggle">
            <label class="switch">
                <input
                    id="promotion-active" type="checkbox"
                    :disabled="toggleDisabled"
                    :checked="promotionActivated"
                    v-on:change="$emit('toggle')"
                >
                <span class="slider" />
            </label>
        </div>
    </div>
    <div class="bundle-info">
        <div v-if="!promotionActivated" class="promotion-inactive">
            <div class="wordmark">
                <NuxtImg v-if="hasPp11" src="/images/promotions/bigbangbundle/scramble-logo-bw.png" />
                <NuxtImg v-if="hasSurveyScramble" src="/images/promotions/bigbangbundle/pp11-logo-bw.png" />
            </div>
            <div class="price">
                <div class="normal">Normal Price: <span class="strikethrough">{{ upsaleProductCompareAtPrice }}</span></div>
                <div class="bundled">Bundled: {{ upsaleProductDiscountedPrice }}</div>
            </div>
        </div>
        <div v-else class="promotion-active">
            <div class="title">The Big Bang Bundle:</div>
            <div class="wordmark-container">
                <div class="wordmarks">
                    <NuxtImg src="/images/promotions/bigbangbundle/scramble-logo-bw.png" />
                    <div class="plus">+</div>
                    <NuxtImg src="/images/promotions/bigbangbundle/pp11-logo-bw.png" />
                </div>
            </div>
        </div>
    </div>
</div>
</template>

<script setup lang="ts">
import { type ComputedRef, type PropType } from 'vue'
import Shopify from '@jackboxgames/shopify'
import { formatPrice } from '$services/storefronts/storefronts'

const props = defineProps({
    promotionActivated: { type: Boolean, required: true },
    toggleDisabled: { type: Boolean, required: true },
    hasPp11: { type: Boolean, required: true },
    hasSurveyScramble: { type: Boolean, required: true },
    upsaleProduct: { type: Object as PropType<Shopify.Shopify.Product | null>, default: null },
    currentProduct: { type: Object as PropType<Shopify.Shopify.Product | null>, default: null },
    bigBangBundle: { type: Object as PropType<Shopify.Shopify.Product | null>, default: null },
    discountedPriceNumeric: { type: Number, required: true }
})

defineEmits(['toggle'])

const app = useNuxtApp()

const upsaleProductCompareAtPrice: ComputedRef<string> = computed(() => {
    if (!props.upsaleProduct) {
        return ''
    }
    const currencyCode = props.upsaleProduct.variants[0].price.currencyCode
    const maxPrice = props.upsaleProduct.compareAtPriceRange.maxVariantPrice.amount
    return formatPrice(maxPrice, currencyCode, app.$i18n.locale)
})

const upsaleProductDiscountedPrice: ComputedRef<string> = computed(() => {
    if (!props.currentProduct || !props.bigBangBundle) {
        return ''
    }
    const currencyCode = props.bigBangBundle.variants[0].price.currencyCode
    return formatPrice(`${props.discountedPriceNumeric}`, currencyCode, app.$i18n.locale)
})
</script>

<style scoped lang="scss">
@use "$styles/kit.scss" as *;

.promotion-container {
    margin: 16px -17px 0 -24px;
    border-top: 1px solid #515E71;
    background: var(--Surface-50, #3A3C43);
}

.promotion {
    display: flex;
    flex-direction: row;
    padding: 0 17px 0 24px;
    @media only screen and (max-width: $mq-cutoff-xsmall) {
        & {
            padding: 0 8px;
        }
    }
    .wordmark {
        width: 94px;
        img {
            width: 83px;
        }
    }
    .description {
        flex: 1;
        padding: 12px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        @media only screen and (max-width: $mq-cutoff-xsmall) {
            & {
                padding: 8px;
                font-size: 14px;
            }
        }
        .header {
            font-weight: 600;
            margin-bottom: 12px;
        }
    }
    .toggle {
        width: 120px;
        display: flex;
        flex-direction: column;
        align-items: center;
        margin-top: 22px;

        @media only screen and (max-width: $mq-cutoff-xsmall) {
            & {
                width: 62px;
            }
        }

        /* The switch - the box around the slider */
        .switch {
            position: relative;
            display: inline-block;
            width: 55px;
            height: 22px;

            input {
                opacity: 0;
                width: 0;
                height: 0;
                &:checked + .slider {
                    background-color: #D86500;
                }
                &:focus + .slider {
                    box-shadow: 0 0 1px #D86500;
                }
                &:disabled + .slider {
                    background-color: var(--neutral-600);
                    cursor: not-allowed;
                }
                &:checked + .slider:before {
                    transform: translateX(23px);
                    background-color: #FFBA1A;
                    border: 3px solid #D86500;
                }
                &:disabled + .slider:before {
                    cursor: not-allowed;
                    transform: translateX(23px);
                    background-color: var(--neutral-400);
                    border: 3px solid var(--neutral-500);
                }
            }

        }

        /* The slider */
        .slider {
            position: absolute;
            cursor: pointer;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            border-radius: 16px;
            background-color: var(--neutral-400);
            transition: .25s;
            &:before {
                position: absolute;
                content: "";
                height: 32px;
                width: 32px;
                left: 0;
                bottom: -5px;
                border-radius: 50%;
                background-color: var(--neutral-200);
                border: 3px solid var(--neutral-300);
                transition: .25s;
                filter: drop-shadow(0 4px 6.667px rgba(0, 0, 0, 0.02)) drop-shadow(0 0 2.667px rgba(0, 0, 0, 0.05))
                    drop-shadow(0 1.333px 5.333px rgba(0, 0, 0, 0.08));
            }
        }
    }
}

.bundle-info {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0 17px 0 24px;
    height: 54px;
    background-image: url('/images/promotions/bigbangbundle/bigbang-bot-bg.png');
    border-bottom: 1px solid #515E71;
    background-size: cover;
    .promotion-inactive {
        height: 100%;
        display: flex;
        flex-direction: row;
        gap: 40px;
        .wordmark {
            display: flex;
            flex-direction: column;
            justify-content: center;
            width: 80px;
            padding: unset;
            margin-left: 8px;
            img {
                width: 100%;
            }
        }
        .price {
            height: 100%;
            display: flex;
            flex-direction: column;
            justify-content: center;
            gap: 6px;
            .normal {
                font-size: 14px;
                font-weight: 500;
                line-height: 14px;
                .strikethrough {
                    text-decoration: line-through;
                }
            }
            .bundled {
                font-size: 16px;
                font-weight: 600;
                line-height: 16px;
            }
        }
    }
    .promotion-active {
        display: flex;
        flex-direction: row;
        height: 100%;
        gap: 16px;
        .title {
            display: flex;
            flex-direction: column;
            justify-content: center;
            height: 100%;
            font-size: 16px;
            line-height: 16px;
            font-weight: 600;
        }
        .wordmark-container {
            display: flex;
            flex-direction: column;
            justify-content: center;
            height: 100%;
            .wordmarks {
                display: flex;
                flex-direction: row;
                justify-content: center;
                gap: 4px;
                img {
                    height: 40px;
                }
                .plus {
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    font-size: 16px;
                    font-weight: 800;
                    line-height: 16px;
                }
            }
        }
    }
}
</style>
